import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { useDispatch } from "react-redux";
import { login } from "../../../user";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config";
import login_icon from "../../../assets/img/login_icon.png";
import createCustomSchema from "../../../fieldValidator/fieldValidator";
import { isValidString } from "../../../utils/validateDateFields";
import { capitalizeWords } from "../../../utils/basicFunctions";
import FindCompanyModal from "../../../components/modelpopup/FindCompanyModal";
import CompanyCoordinatesError from "./CompanyCoordinatesError";
import Loader from "../../../components/Loader";
import { onLoginSuccess } from "./TrackingHandler";
import {
  fetchCompanySubscription,
  checkSubscriptionExpiry,
} from "../../../services/SubscriptionService";
import { setThemeColor } from "../../../utils/setCompanyThemeColor";
import { useAuth } from "../../../context/AuthContext";
import { clearAuthToken, setAuthToken } from "../../../utils/tokenStorage";
import { clearRefreshTokenExpiry } from "../../../utils/sessionExpiry";
import { getEmployeeTimeZoneDate } from "../../../utils/getEmployeeTimeZoneDate";
import axios from "axios";
import Cookies from "js-cookie";
import CompanySelectorModal from "../../../components/CompanySelectorModal";
import { getFile } from "../../../utils/getFile";
import GoogleRecaptcha from "../../../components/GoogleRecaptcha/GoogleRecaptcha";
const Login = () => {
  const { setIsAuthenticated } = useAuth();
  const [validCompanyDetails, setValidCompanyDetails] = useState([]);
  const [companyUrl, setCompanyUrl] = useState(false);
  const { company_name } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [emailError, setEmailError] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyColor, setCompanyColor] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [emailCount, setEmailCount] = useState(0);
  const [emailCountError, setEmailCountError] = useState(false);
  const [isInValidPassword, setIsInValidPassword] = useState(false);
  const [role, setRole] = useState(null);
  const [locationErrorMessage, setLocationErrorMessage] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workLocation, setWorkLocation] = useState(null);
  const [error, setError] = useState({
    subscriptionExpired: null,
    responseError: null,
  });

  const [chooseCompany, setChooseCompany] = useState({
    show: false,
    companies: [],
  });
  const [employeeId, setEmployeeId] = useState(null);
  const [multipleEmployer, setMultipleEmployer] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);

  const closeChooseCompany = () => {
    setChooseCompany({ ...chooseCompany, show: false });
  };
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const passwordInputRef = useRef(null);

  // Focus the password input when step changes to 2
  const focusPasswordInput = () => {
    if (passwordInputRef.current) {
      passwordInputRef.current.setAttribute("tabindex", "0");
      if (passwordInputRef.current.disabled || passwordInputRef.current.readOnly) {
        console.log("Password input is disabled or readOnly");
        return false;
      }
      passwordInputRef.current.focus();
      // Verify if the input is focused
      if (document.activeElement === passwordInputRef.current) {
        console.log("Password input successfully focused");
        return true;
      } else {
        console.log("Failed to focus password input");
        // Fallback: Try selecting the input via DOM query
        const fallbackInput = document.querySelector(".form-control[type='password']") || 
                             document.querySelector(".form-control[type='text']");
        if (fallbackInput) {
          fallbackInput.focus();
          console.log("Fallback focus attempt on password input");
          if (document.activeElement === fallbackInput) {
            console.log("Fallback input successfully focused");
            return true;
          }
        }
        return false;
      }
    } else {
      console.log("Password input ref is not available");
      return false;
    }
  };

  // Focus the password input when step changes to 2 with retry logic
  useEffect(() => {
    if (step === 2) {
      
      // Initial focus attempt
      focusPasswordInput();
      // Retry loop to handle rendering delays
      let attempts = 0;
      const maxAttempts = 5;
      const retryFocus = setInterval(() => {
        if (focusPasswordInput() || attempts >= maxAttempts) {
          clearInterval(retryFocus);
        }
        attempts++;
        console.log(`Focus retry attempt ${attempts}/${maxAttempts}`);
      }, 200);
      return () => clearInterval(retryFocus);
    }
  }, [step]);

  const getSubscriptionStatus = async (companyId) => {
    setLoading(true);
    const error_message =
      "Please reach out to your administrator for assistance.";
    try {
      const subscription = await fetchCompanySubscription(companyId);
      const expired = checkSubscriptionExpiry(subscription);
      localStorage.setItem("isSubscriptionExpired", JSON.stringify(expired));
      if (expired) {
        setError((prev) => ({
          ...prev,
          subscriptionExpired: error_message,
        }));
      }
      return expired;
    } catch (err) {
      setError((prev) => ({
        ...prev,
        subscriptionExpired: error_message,
      }));
      return true;
    } finally {
      setLoading(false);
    }
  };

  const handleCoordinatesErrorModal = () => {
    setChooseCompany({ ...chooseCompany, show: false });
    setShowModal(false);
  };

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    if (company_name && isValidString(company_name)) {
      axios
        .get(`${config.API_BASE_URL}auth/login/${company_name}`)
        .then((res) => {
          const companyInfo = res?.data?.result;
          if (res?.data?.success == true) {
            setValidCompanyDetails(companyInfo);
            if (companyInfo?.length) {
              setThemeColor(companyInfo[0]?.colour);
              const company_url = companyInfo[0]?.company_name
                ?.trim()
                ?.replace(/\s+/g, "-")
                .toLowerCase();
              localStorage.setItem("company_url", company_url);
            }
          }
        })
        .catch((err) => console.error(err));
    }
  }, [companyUrl, company_name]);

  const validationSchema = Yup.object().shape({
    email: createCustomSchema("email", {
      message: "Please enter Your  Email.",
    }),
  });

  const {
    getValues,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [authEmailRouteError, setAuthEmailRouteError] = useState("");
  const [findCompanyName, setFindCompanyName] = useState("");
  const [doneNavigation, setDoneNavigation] = useState(false);

  const handleCompanySelection = (company, multipleCompany) => {
    const responseCompanyName = company.company_name
      ?.trim()
      ?.replace(/\s+/g, "-")
      .toLowerCase();
    const roleId = company.role_id;

    setRole(roleId);
    setCompanyId(company.company_id);
    setWorkLocation(company.work_location);
    if (
      responseCompanyName !== company_name &&
      roleId !== 1 &&
      !multipleCompany
    ) {
      setFindCompanyName(responseCompanyName);
      let message;
      if (company_name) {
        message = `*Your Account does not Exists in ${capitalizeWords(
          company_name
        )}. Please Login with correct URL or connect with your company administration! `;
      } else {
        message =
          "Please Login with correct URL or connect with your company administration!";
      }

      setAuthEmailRouteError(message);
      setStep(1);
      navigate(window.location.pathname, { replace: true });
      setCompanyUrl(true);
      setLoading(false);
      return false;
    }
    setLoading(false);
    return true;
  };

  const validateEmail = async (email) => {
    setLoading(true);
    setAuthEmailRouteError("");
    try {
      const trimmedEmail = email.trim();
      const response = await axios.post(
        `${config.API_BASE_URL}auth/validate-email`,
        {
          email: trimmedEmail,
          // captcha: recaptchaToken
        }
      );
      const multipleCompany = response?.data?.multipleCompany;
      localStorage.setItem("multipleEmployer", multipleCompany ? 1 : 0);
      if (multipleCompany) {
        setChooseCompany({ show: false, ...response?.data });
        setMultipleEmployer(true);
      }
      const result = handleCompanySelection(
        response?.data?.companies[0],
        multipleCompany
      );
      if (result) {
        setEmployeeId(response?.data?.companies[0]?.employee_id);
        return response?.data;
      }
      return false;
    } catch (error) {
      setLoading(false);
      return false;
    } finally {
      // setRecaptchaToken("");
      // window.grecaptcha.reset();
    }
  };

  const handleEmailSubmit = async (data) => {
    setLoading(true);
    setValue("email", data.email);

    if (!data?.email) {
      setEmailError(true);
      setLoading(false);
      return;
    }
    // if (!recaptchaToken) {
    //   setCaptchaError(true);
    //   setLoading(false);
    //   return;
    // }
    const emailCheckResult = await validateEmail(data.email);
    const { companies = [] } = emailCheckResult || {};
    const {
      work_location: employeeWorkLocation = null,
      company_id: employeeCompanyId = null,
    } = companies[0] || {};
    localStorage.setItem("employeeOfficialEmail", data?.email);
    localStorage.setItem("work_location", employeeWorkLocation);
    // let inLocation = null;
    // const inMultipleCompany = localStorage.getItem("multipleEmployer") === "1";
    // if (employeeWorkLocation == 1 && !inMultipleCompany) {
    //   inLocation = await getEmployeeCurrentPosition(employeeCompanyId);
    // }
    // if (inLocation || employeeWorkLocation != 1 || inMultipleCompany) {
    setLoading(false);
    if (emailCheckResult?.exists) {
      setLoading(false);
      if (emailCheckResult?.newEmployee === false) {
        const company_url = emailCheckResult?.companies[0]?.company_name
          ?.trim()
          ?.replace(/\s+/g, "-")
          .toLowerCase();
        localStorage.setItem("company_url", company_url);
        getSubscriptionStatus(employeeCompanyId)
          .then((isExpired) => {
            if (!isExpired || emailCheckResult?.companies[0]?.role_id == 1) {
              setStep(2);
              passwordInputRef.current.focus();
              if (passwordInputRef.current) {
                passwordInputRef.current.focus();
              }

              setEmailCountError(false);
              setError({
                subscriptionExpired: null,
                responseError: null,
              });
            }
          })
          .catch((error) => {
            console.error("Error checking subscription status:", error);
          });
      } else {
        navigate("/forgot-password", {
          state: {
            companyLogo: `${config.IMAGE_URL}${validCompanyDetails[0]?.company_logo}`,
            companyThemeColor: validCompanyDetails[0]?.colour,
            companyName: validCompanyDetails[0]?.company_name,
          },
        });
      }
    } else {
      setLoading(false);
      if (emailCount > 2) {
        setEmailCountError(true);
        setEmailError(false);
      } else {
        setEmailError(true);
        setEmailCount((prev) => prev + 1);
        setEmailCountError(false);
      }
    }
    // } else {
    //   setLoading(false);
    // }
  };

  const punchDate = getEmployeeTimeZoneDate();

  const verifyPassword = async (data) => {
    setLoading(true);
    // if (!recaptchaToken) {
    //   setCaptchaError(true);
    //   setLoading(false);
    //   return;
    // }
    try {
      await axios.post(
        `${config.API_BASE_URL}auth/verify-password`,
        {
          ...data,
          employee_id: employeeId,
          // captcha: recaptchaToken
        },
        { withCredentials: true }
      );
      if (multipleEmployer) {
        setChooseCompany({ ...chooseCompany, show: true });
      } else {
        await handleLoginSubmit(data, workLocation);
      }
    } catch (error) {
      setEmailError(true);
      setIsInValidPassword(true);
    } finally {
      // setRecaptchaToken("");
      // window.grecaptcha.reset();
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (data, workLocation) => {
    setLoading(true);
    if (!data?.password) {
      setPasswordError("* Please enter password!");
      setIsInValidPassword(false);
      setLoading(false);
      return;
    }
    setPasswordError("");
    let inLocation = null;
    // if (workLocation == 1) {
    //   inLocation = await getEmployeeCurrentPosition(companyId);
    // }
    // if (inLocation || workLocation != 1) {
    setLoading(false);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}auth/login`,
        {
          ...data,
          employee_id: employeeId,
          punchDate: punchDate,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        setAuthToken(response.data.accessToken);
        const { token, user } = response.data;
        const responseCompanyName = user?.company?.name
          ?.trim()
          ?.replace(/\s+/g, "-")
          .toLowerCase();
        const sessionDuration = response.data.sessionDuration;
        const refreshTokenExpiry = Date.now() + sessionDuration;
        Cookies.set("refreshTokenExpiry", `${refreshTokenExpiry}`, {
          secure: true,
          sameSite: "None",
          path: "/",
          expires: sessionDuration / (1000 * 60 * 60 * 24),
        });
        localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("refreshTokenExpiry", refreshTokenExpiry);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("colorschema", user.company.colour);
        localStorage.setItem("companyColor", user.company.colour);
        localStorage.setItem("companyName", user.company.name);
        localStorage.setItem("employee_role", user?.employee_role);
        dispatch(login(user));
        onLoginSuccess(user?.id, user?.work_start_time, user?.work_end_time);
        setIsAuthenticated(true);
        navigate(`../${responseCompanyName}/`);
      } else {
        setEmailError(true);
        setIsInValidPassword(true);
      }
    } catch (error) {
      console.error("Error during login", error);
      setEmailError(true);
      setIsInValidPassword(true);
    }
    // } else {
    //   setLoading(false);
    // }
  };

  useEffect(() => {
    setValue("email", localStorage.getItem("employeeOfficialEmail"));
    setValue("password", localStorage.getItem("password"));
  }, []);

  const [eye, seteye] = useState(true);

  const onEyeClick = () => {
    seteye(!eye);
  };

  useEffect(() => {
    const company_url = localStorage.getItem("company_url");
    if (isAuthenticated) {
      navigate(window.location.pathname, { replace: true });
      return;
    }
    if (company_url) {
      navigate(`../${company_url}`);
      return;
    }
  }, [isAuthenticated, company_name]);

  useEffect(() => {
    setAuthEmailRouteError("");
    setEmailError(false);
  }, [doneNavigation]);

  const getEmployeeCurrentPosition = async (company_id) => {
    setLoading(true);
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        setLoading(false);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            const locationAllowed = await checkEmployeeLocation(
              latitude,
              longitude,
              company_id
            );
            resolve(locationAllowed);
          },
          (error) => {
            setShowModal(true);
            setLocationErrorMessage(
              "Failed to retrieve location. Turn on location and give access to the app."
            );
            console.error("Location error:", error);
            resolve(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      } else {
        setLoading(false);
        setLocationErrorMessage(
          "Geolocation is not supported by your browser."
        );
        resolve(false);
      }
    });
  };

  const checkEmployeeLocation = async (latitude, longitude, company_id) => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}auth/company_coordinates`,
        {
          latitude,
          longitude,
          company_id,
        }
      );
      if (response.data.allowed) {
        setShowModal(false);
        return true;
      } else {
        setShowModal(true);
        setLocationErrorMessage(
          "You are not within the allowed location. Try to locate near company radius."
        );
        return false;
      }
    } catch (error) {
      setLocationErrorMessage("Error checking location.");
      return false;
    } finally {
    }
  };

  useEffect(() => {
    const fetchImage = async () => {
      let image = null;
      if (validCompanyDetails[0]?.company_logo) {
        const imageUrl = await getFile(
          `${config.IMAGE_URL}${validCompanyDetails[0]?.company_logo}`
        );
        if (imageUrl) {
          image = imageUrl;
        }
      }
      setCompanyLogo(image);
    };

    fetchImage();
  }, [validCompanyDetails[0]?.company_logo]);

  if (isAuthenticated) {
    const companyName = localStorage.getItem("companyName");
    if (!companyName) {
      setIsAuthenticated(false);
      clearAuthToken();
      clearRefreshTokenExpiry();
      navigate("/");
      return;
    }
    navigate(`../${companyName.trim()?.replace(/\s+/g, "-").toLowerCase()}`);
    return;
  }
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setCaptchaError(false);
  };
  return (
    <>
      <div
        className="account-page"
        style={{
          backgroundColor: validCompanyDetails[0]?.colour,
          overflow: "auto",
        }}
      >
        <div className="main-wrapper">
          <div className="account-content">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="btn login-back-btn">
                <i class="fa-solid fa-arrow-left" /> Back
              </button>
            )}
            <div className="container">
              <div className="account-logo">
                <Link to="#">
                  <img
                    src={companyLogo ?? login_icon}
                    alt={validCompanyDetails[0]?.company_name || "app logo"}
                    style={{
                      borderRadius: "50%",
                      zIndex: "100",
                      backgroundColor: "white",
                      width: "150px",
                      height: "150px",
                      padding: "2px",
                    }}
                  />
                </Link>
              </div>
              <div className="account-box">
                <div className="account-wrapper">
                  <h3 className="account-title">Login</h3>
                  <p className="account-subtitle">
                    Access to{" "}
                    {validCompanyDetails[0]?.company_name
                      ? capitalizeWords(validCompanyDetails[0]?.company_name)
                      : "your"}{" "}
                    dashboard
                  </p>
                  <div>
                    {step === 1 ? (
                      <form onSubmit={handleSubmit(handleEmailSubmit)}>
                        <div className="input-block mb-4">
                          <label className="col-form-label">
                            Email Address
                          </label>
                          <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                              <input
                                className={`form-control ${emailError ? "error-input" : ""
                                  }`}
                                type="text"
                                onChange={field.onChange}
                                value={field.value}
                                autoComplete="true"
                                autoFocus={true}
                              />
                            )}
                          />
                          <span className="text-danger">
                            {errors.email?.message ? "* " : ""}
                            {(authEmailRouteError && authEmailRouteError) ||
                              errors.email?.message ||
                              (emailError && "*Please Enter Valid Email!") ||
                              (emailCountError &&
                                "*Please Contact Your Organisation !")}
                          </span>
                          {error?.subscriptionExpired ? (
                            <span className="text-danger">
                              *{error?.subscriptionExpired}
                            </span>
                          ) : (
                            <></>
                          )}
                        </div>
                        <div className="input-block text-center">
                          <button
                            className="btn btn-primary account-btn"
                            type="submit"
                          >
                            Validate email
                          </button>
                        </div>
                        {authEmailRouteError && (
                          <>
                            Help -{" "}
                            <Link
                              to="#"
                              data-bs-toggle="modal"
                              data-bs-target="#find_company"
                              style={{ color: "#201a7a", fontSize: "14px" }}
                            >
                              Find Your Company URL
                            </Link>
                          </>
                        )}
                      </form>
                    ) : (
                      <form onSubmit={handleSubmit(verifyPassword)}>
                        <div className="input-block mb-4">

                          <div className="row">
                            <div className="col-12">
                              <label className="col-form-label">
                                Email Address
                              </label>
                            </div>
                            <div className="col-12">
                              <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    className={`form-control ${emailError ? "error-input" : ""
                                      }`}
                                    type="text"
                                    // onChange={field.onChange}
                                    value={field.value}
                                    autoComplete="true"

                                    disabled={true}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="row mt-2">

                            <div className="col">
                              <label className="col-form-label">Password</label>
                            </div>

                          </div>


                          <div style={{ position: "relative" }} >
                            <div className="">
                              <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    className={`form-control ${errors?.password ? "error-input" : ""
                                      }`}
                                    type={eye ? "password" : "text"}
                                    onChange={field.onChange}
                                    value={field.value}
                                    ref={passwordInputRef}
                                  autoFocus={true}
                                  autoComplete={false}
                                  />
                                )}
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  right: "4%",
                                  top: "30%",
                                }}
                                onClick={onEyeClick}
                                className={` cursor-pointer fa-solid ${eye ? "fa-eye-slash" : "fa-eye"
                                  }`}
                              />
                            </div>
                          </div>

                          <span className="text-danger">
                            {errors.password?.message || passwordError}
                          </span>
                          {isInValidPassword && (
                            <span className="text-danger">
                              * Please enter correct password.
                            </span>
                          )}
                          <div className="row">
                            <div className=" text-end">
                              <Link
                                className="text-muted"
                                to="/forgot-password"
                                state={{
                                  companyLogo: `${config.IMAGE_URL}${validCompanyDetails[0]?.company_logo}`,
                                  companyThemeColor:
                                    validCompanyDetails[0]?.colour,
                                  companyName:
                                    validCompanyDetails[0]?.company_name,
                                }}
                              >
                                Forgot password?
                              </Link>
                            </div>

                          </div>



                        </div>
                        <div className="input-block text-center">
                          <button
                            className="btn btn-primary account-btn"
                            type="submit"
                          >
                            Login
                          </button>
                        </div>
                      </form>
                    )}
                    {/* <div>
                      <GoogleRecaptcha onChange={handleRecaptchaChange} />
                      <p className="text-danger">
                        {captchaError ? "*Please Confirm Captcha!" : ""}
                      </p>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FindCompanyModal
        findCompanyName={findCompanyName}
        setDoneNavigation={setDoneNavigation}
      />
      <CompanyCoordinatesError
        errorMessage={locationErrorMessage}
        show={showModal}
        handleClose={handleCoordinatesErrorModal}
      />
      <Loader show={loading} loadingMessage="" />
      {!showModal && (
        <CompanySelectorModal
          show={chooseCompany.show}
          onHide={closeChooseCompany}
          onContinue={(selectedCompany) =>
            handleLoginSubmit(
              {
                email: getValues("email"),
                password: getValues("password"),
              },
              selectedCompany?.WorkLocation
            )
          }
          companies={chooseCompany?.companies}
          setEmployeeId={setEmployeeId}
          setWorkLocation={setWorkLocation}
        />
      )}
    </>
  );
};

export default Login;
